/**
 * Pure helpers between the org pages and @hub/lib/org-types. This package
 * has no component-rendering tests, so every decision a page makes lives
 * here and is tested without a DOM (same shape as AddInstancePage's
 * agentFormError).
 */
import type { OrgProfile, Validated } from "@hub/lib/org-types";
import { validateOrgProfile } from "@hub/lib/org-types";

export interface ProfileForm {
  name: string;
  legalName: string;
  description: string;
  industry: string;
  /** Comma-separated. */
  emailDomains: string;
  /** JSON text: OrgTeam[]. */
  teams: string;
  /** JSON text: OrgGlossaryEntry[]. */
  glossary: string;
  writingStyle: string;
  primaryColor: string;
  secondaryColor: string;
  logoUrl: string;
  fontFamily: string;
  documentFooter: string;
  supportContact: string;
  assistantName: string;
}

export function emptyProfileForm(): ProfileForm {
  return {
    name: "",
    legalName: "",
    description: "",
    industry: "",
    emailDomains: "",
    teams: "[]",
    glossary: "[]",
    writingStyle: "",
    primaryColor: "#001847",
    secondaryColor: "#1E6CC7",
    logoUrl: "",
    fontFamily: "",
    documentFooter: "",
    supportContact: "",
    assistantName: "",
  };
}

/** Builds fresh strings from the profile; nothing in `profile` is shared or mutated. */
export function profileFormFrom(profile: OrgProfile): ProfileForm {
  return {
    name: profile.name,
    legalName: profile.legalName ?? "",
    description: profile.description,
    industry: profile.industry,
    emailDomains: profile.emailDomains.join(", "),
    teams: JSON.stringify(profile.teams, null, 2),
    glossary: JSON.stringify(profile.glossary, null, 2),
    writingStyle: profile.writingStyle,
    primaryColor: profile.brand.primaryColor,
    secondaryColor: profile.brand.secondaryColor,
    logoUrl: profile.brand.logoUrl ?? "",
    fontFamily: profile.brand.fontFamily ?? "",
    documentFooter: profile.brand.documentFooter,
    supportContact: profile.supportContact,
    assistantName: profile.assistantName ?? "",
  };
}

function parseJsonList(text: string, field: string): Validated<unknown[]> {
  try {
    const parsed: unknown = JSON.parse(text.trim() || "[]");
    if (!Array.isArray(parsed)) return { ok: false, error: `${field} must be a JSON list` };
    return { ok: true, value: parsed };
  } catch {
    return { ok: false, error: `${field} is not valid JSON` };
  }
}

export function profileFromForm(form: ProfileForm): Validated<OrgProfile> {
  const teams = parseJsonList(form.teams, "teams");
  if (!teams.ok) return teams;
  const glossary = parseJsonList(form.glossary, "glossary");
  if (!glossary.ok) return glossary;
  return validateOrgProfile({
    name: form.name,
    legalName: form.legalName || null,
    description: form.description,
    industry: form.industry,
    emailDomains: form.emailDomains
      .split(",")
      .map((d) => d.trim())
      .filter(Boolean),
    teams: teams.value,
    glossary: glossary.value,
    writingStyle: form.writingStyle,
    brand: {
      primaryColor: form.primaryColor,
      secondaryColor: form.secondaryColor,
      logoUrl: form.logoUrl || null,
      fontFamily: form.fontFamily || null,
      documentFooter: form.documentFooter,
    },
    supportContact: form.supportContact,
    assistantName: form.assistantName || null,
  });
}

/** "for 3d 4h" / "for 30m" since the version reached its channel. */
export function channelSince(sinceIso: string | null, now: number = Date.now()): string {
  if (!sinceIso) return "nothing published";
  const minutes = Math.max(0, Math.floor((now - Date.parse(sinceIso)) / 60000));
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  if (days > 0) return `for ${days}d ${hours}h`;
  if (hours > 0) return `for ${hours}h ${minutes % 60}m`;
  return `for ${minutes}m`;
}

export function pinLabel(row: {
  pinnedVersion: string | null;
  orgPinnedVersion: string | null;
  orgChannel: string;
}): string {
  if (row.pinnedVersion) return `Pinned to ${row.pinnedVersion}`;
  if (row.orgPinnedVersion) return `Org pinned to ${row.orgPinnedVersion}`;
  return `Follows ${row.orgChannel === "stable" ? "Stable" : "Early"}`;
}
